import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import type { DeleteChildExperienceInput } from '@/types/gql.generated';
import { useToast } from '@/ui';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  DeleteChildExperienceMutation,
  DeleteChildExperienceMutationVariables,
} from './useDeleteChildExperience.generated';

const document = gql`
  mutation DeleteChildExperience($input: DeleteChildExperienceInput!) {
    deleteChildExperience(input: $input)
  }
`;

export const useDeleteChildExperience = (parentExperienceId: string) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation<
    DeleteChildExperienceMutation,
    QueryError,
    DeleteChildExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        DeleteChildExperienceMutation,
        DeleteChildExperienceMutationVariables
      >(document, variables),
    // Return the invalidation promise so isPending stays true until the parent
    // refetch settles, keeping the removed view visible (in its "Removing…"
    // state) until the list actually updates rather than flashing.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(parentExperienceId),
      }),
    onError: () => {
      toast.error('Failed to remove view. Please try again.');
    },
  });

  const deleteChildExperience = useCallback(
    (
      childExperienceId: string,
      options?: MutateOptions<
        DeleteChildExperienceMutation,
        QueryError,
        DeleteChildExperienceMutationVariables
      >
    ) => {
      const input: DeleteChildExperienceInput = {
        experienceId: childExperienceId,
      };
      return mutate({ input }, options);
    },
    [mutate]
  );

  return { deleteChildExperience, isPending };
};
